<template>
  <main class="create-edit-customer-container">
    <div class="create-edit-customer-card">
      <div class="card__header">
        <span class="card__header--topic">{{ cardTitle }}</span>
      </div>
      <div class="card__body">
        <div class="mb-2 input-container">
          <atom-input
            v-model="currentCustomerDetail.customerName"
            title="Customer name"
            placeholder="Customer name"
            :isDisable="!hasUpdateNtdcCustomerPermission"
            :isAsterisk="true"
            :isError="isErrorAtNewCustomerName"
            :errorMessage="errorMessageAtInput"
          >
          </atom-input>
        </div>
        <div name="addresses-card" class="row">
          <div name="main-address-card" class="col-12 col-lg-4">
            <molecule-create-edit-address-card
              v-model="currentCustomerDetail.mainAddress"
              addressType="MAIN ADDRESS"
              :hasEditPermission="hasUpdateNtdcCustomerPermission"
              :businessTypeOption="businessTypeOption"
              :isValidateAddressTrigger="isValidateTrigger"
              @onUpdateErrorInCreateEditAddress="onUpdateErrorInMainAddress"
            ></molecule-create-edit-address-card>
          </div>
          <div name="billing-address-card" class="col-12 col-lg-4 mt-3 mt-lg-0">
            <molecule-create-edit-address-card
              v-model="currentCustomerDetail.billingAddress"
              addressType="BILLING ADDRESS"
              :hasEditPermission="hasUpdateNtdcCustomerPermission"
              :businessTypeOption="businessTypeOption"
              :isValidateAddressTrigger="isValidateTrigger"
              :main-address-detail="currentCustomerDetail.mainAddress"
              @onVatValueChange="onVatValueChange"
              @onUpdateErrorInCreateEditAddress="onUpdateErrorInBillingAddress"
            ></molecule-create-edit-address-card>
          </div>
          <div name="mailing-address-card" class="col-12 col-lg-4 mt-3 mt-lg-0">
            <molecule-create-edit-address-card
              v-model="currentCustomerDetail.mailingAddress"
              addressType="MAILING ADDRESS"
              :hasEditPermission="hasUpdateNtdcCustomerPermission"
              :businessTypeOption="businessTypeOption"
              :isValidateAddressTrigger="isValidateTrigger"
              :main-address-detail="currentCustomerDetail.mainAddress"
              @onUpdateErrorInCreateEditAddress="onUpdateErrorInMailingAddress"
            ></molecule-create-edit-address-card>
          </div>
        </div>
        <div
          v-if="hasUpdateNtdcCustomerPermission"
          class="d-flex justify-content-end mt-2 confirm-add-new-customer-button-container"
        >
          <atom-button
            v-if="!customerId"
            buttonId="confirm-add-customer-button"
            :isDisable="isDisableCreateNewCustomerButton"
            @click="onClickConfirmCreateButton"
          >
            Create New
          </atom-button>
          <atom-button
            v-else
            buttonId="save-change-customer-button"
            @click="onClickConfirmSaveChangeButton"
          >
            Save Change
          </atom-button>
          <atom-button
            class="ml-1"
            buttonId="cancel-add-customer-button"
            buttonStyle="border"
            buttonColor="secondary"
            @click="onClickCancelCreateEditButton"
          >
            Cancel
          </atom-button>
        </div>
      </div>
    </div>
    <footer style="height: 12px"></footer>

    <!-- start modal section -->
    <!-- start confirm create new customer modal -->
    <molecule-modal-dialog
      modalId="modal-confirm-create-new-customer"
      :isTextCenter="true"
      :textTitle="`Do you want to create new customer ?`"
      :textSubtitle="`“${currentCustomerDetail.customerName}” will add to Customer list.`"
      buttonTextLeft="Confirm"
      @clickButtonLeft="onClickConfirmCreateFromModal"
    ></molecule-modal-dialog>
    <!-- end confirm create new customer modal -->

    <!-- start confirm edit customer modal -->
    <molecule-modal-dialog
      modalId="modal-confirm-edit-customer"
      :isTextCenter="true"
      :textTitle="`Do you want to edit customer ?`"
      :textSubtitle="`“${currentCustomerDetail.customerName}” will be edited.`"
      buttonTextLeft="Save Change"
      @clickButtonLeft="onClickConfirmEditFromModal"
    ></molecule-modal-dialog>
    <!-- end confirm edit customer modal -->

    <!-- start confirm discard change modal -->
    <molecule-modal-dialog
      modalId="modal-confirm-discard-change"
      :isTextCenter="true"
      textTitle="Do you want to leave without finishing?"
      buttonTextLeft="Confirm"
      @clickButtonLeft="onClickConfirmDiscardChangeFromModal"
    >
    </molecule-modal-dialog>
    <!-- end confirm discard change modal -->
  </main>
</template>

<script>
const replacer = (key, value) =>
  value instanceof Object && !(value instanceof Array)
    ? Object.keys(value)
        .sort()
        .reduce((sorted, key) => {
          sorted[key] = value[key]
          return sorted
        }, {})
    : value

import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import MoleculeCreateEditAddressCard from "@/components/molecules/portal/idc/MoleculeCreateEditAddressCard.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import mixinsValidator from "@/mixins/mixinsValidator"
import mixinsUser from "@/mixins/mixinsUser"
import { PermissionUser } from "@/constants/permission-user"

export default {
  name: "OrganismIdcCreateEditCustomer",
  components: {
    AtomButton,
    AtomInput,
    MoleculeCreateEditAddressCard,
    MoleculeModalDialog,
  },
  props: {
    cardTitle: {
      type: String,
      required: true,
    },
    businessTypeOption: {
      type: Array,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
  },
  mixins: [mixinsValidator, mixinsUser],
  data() {
    return {
      errorMessageAtInput: "Please enter valid data",
      modalId: null,
      isValidateTrigger: false,
      isErrorOccurInMainAddress: false,
      isErrorOccurInBillingAddress: false,
      isErrorOccurInMailingAddress: false,
      defaultCustomerDetail: {
        id: null,
        customerName: "",
        mainAddress: {
          organizationName: "",
          businessType: null,
          phone: "",
          fax: "",
          email: "",
          country: null,
          province: null,
          district: null,
          subDistrict: null,
          address: "",
          postalCode: null,
        },
        billingAddress: {
          organizationName: "",
          businessType: null,
          phone: "",
          fax: "",
          email: "",
          country: null,
          province: null,
          district: null,
          subDistrict: null,
          address: "",
          postalCode: null,
          vat: 7,
        },
        mailingAddress: {
          organizationName: "",
          businessType: null,
          phone: "",
          fax: "",
          email: "",
          country: null,
          province: null,
          district: null,
          subDistrict: null,
          address: "",
          postalCode: null,
        },
      },
      currentCustomerDetail: {
        id: null,
        customerName: "",
        mainAddress: {
          organizationName: null,
          businessType: null,
          phone: null,
          fax: null,
          email: null,
          country: null,
          province: null,
          district: null,
          subDistrict: null,
          address: null,
          postalCode: null,
        },
        billingAddress: {
          organizationName: null,
          businessType: null,
          phone: null,
          fax: null,
          email: null,
          country: null,
          province: null,
          district: null,
          subDistrict: null,
          address: null,
          postalCode: null,
          vat: 7,
        },
        mailingAddress: {
          organizationName: null,
          businessType: null,
          phone: null,
          fax: null,
          email: null,
          country: null,
          province: null,
          district: null,
          subDistrict: null,
          address: null,
          postalCode: null,
        },
      },
    }
  },
  computed: {
    hasUpdateNtdcCustomerPermission() {
      return this.hasPermission(PermissionUser.WPM.NTDC_CONTRACT_WRITE)
    },

    customerDetail: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit("input", value)
      },
    },
    customerId() {
      return this.$route?.params?.id
    },
    isDisableCreateNewCustomerButton() {
      return this.isValidateTrigger ? this.isErrorOccur : false
    },
    isErrorAtNewCustomerName() {
      return this.isValidateTrigger
        ? this.isEmptyString(this.currentCustomerDetail.customerName)
        : false
    },
    isErrorOccur() {
      return (
        this.isErrorOccurInMainAddress ||
        this.isErrorOccurInBillingAddress ||
        this.isErrorOccurInMailingAddress
      )
    },
  },
  watch: {
    customerDetail: {
      deep: true,
      handler(oldValue, newValue) {
        const isCustomerChange =
          oldValue.customerName !== newValue.customerName ||
          oldValue.customerId !== newValue.customerId
        if (this.customerDetail && isCustomerChange) {
          this.cloneCustomerDetail()
        }
      },
    },
  },
  mounted() {
    if (this.hasString(this.customerId)) {
      this.cloneCustomerDetail()
    } else {
      this.defaultCustomerDetail.mainAddress.country = "Thailand"
      this.defaultCustomerDetail.billingAddress.country = "Thailand"
      this.defaultCustomerDetail.mailingAddress.country = "Thailand"
      this.currentCustomerDetail.mainAddress.country = "Thailand"
      this.currentCustomerDetail.billingAddress.country = "Thailand"
      this.currentCustomerDetail.mailingAddress.country = "Thailand"
    }
  },
  methods: {
    cloneCustomerDetail() {
      Object.assign(this.currentCustomerDetail, this.customerDetail)
    },

    setCurrentToOriginCustomerDetail() {
      Object.assign(this.customerDetail, this.currentCustomerDetail)
    },

    navigateTo(path) {
      this.$router.push(path)
    },

    checkIsDataChangeFromDefault() {
      const defaultData = JSON.stringify(this.defaultCustomerDetail, replacer)
      const currentData = JSON.stringify(this.currentCustomerDetail, replacer)
      this.isDataChange = defaultData === currentData ? false : true
    },

    onVatValueChange(vatValue) {
      this.customerDetail.billingAddress.vat = vatValue
    },

    onClickConfirmCreateButton() {
      this.isValidateTrigger = true
      this.$nextTick(() => {
        if (!this.isErrorOccur && !this.isErrorAtNewCustomerName) {
          this.onShowConfirmCreateModal()
        }
      })
    },

    onClickConfirmSaveChangeButton() {
      this.$nextTick(() => {
        if (!this.isErrorOccur && !this.isErrorAtNewCustomerName) {
          this.onShowConfirmEditModal()
        }
      })
    },

    onClickCancelCreateEditButton() {
      this.checkIsDataChangeFromDefault()
      this.$nextTick(() => {
        if (this.isDataChange) {
          this.modalId = "modal-confirm-discard-change"
          this.$bvModal.show(this.modalId)
        } else {
          this.navigateTo("/internal/web-portal/idc/customer")
        }
      })
    },

    onShowConfirmCreateModal() {
      this.modalId = "modal-confirm-create-new-customer"
      this.$bvModal.show(this.modalId)
    },

    onShowConfirmEditModal() {
      this.modalId = "modal-confirm-edit-customer"
      this.$bvModal.show(this.modalId)
    },

    onUpdateErrorInMainAddress(isErrorOccur) {
      this.isErrorOccurInMainAddress = isErrorOccur
    },

    onUpdateErrorInBillingAddress(isErrorOccur) {
      this.isErrorOccurInBillingAddress = isErrorOccur
    },

    onUpdateErrorInMailingAddress(isErrorOccur) {
      this.isErrorOccurInMailingAddress = isErrorOccur
    },

    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = ""
    },

    onClickConfirmCreateFromModal() {
      this.closeModal()
      this.setCurrentToOriginCustomerDetail()
      this.$emit("onConfirmCreateNewCustomer")
    },

    onClickConfirmEditFromModal() {
      this.closeModal()
      this.setCurrentToOriginCustomerDetail()
      this.$emit("onConfirmEditCustomer")
    },

    onClickConfirmDiscardChangeFromModal() {
      this.closeModal()
      this.navigateTo("/internal/web-portal/idc/customer")
    },
  },
}
</script>

<style lang="scss" scoped>
.create-edit-customer-container {
  width: 100%;
  height: 100%;
}

.create-edit-customer-card {
  background: white;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  padding: 21px;
}

.card__header--topic {
  font-weight: 500;
  font-size: 18px;
}

.card__body {
  margin-top: 14px;
}

.add-new-customer-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
}

.add-new-customer-topic--text {
  font-weight: 500;
  font-size: 14px;
  text-transform: uppercase;
  color: #b9b9c3;
}

.close-icon-container:hover {
  cursor: pointer;
}

.input-container {
  width: 100%;
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
  .input-container {
    width: 325px;
  }
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
}
</style>
